/**
 * The documents browser's wire contract (#6559): the row source behind the
 * Drive-style Documents page and the pickers' Find mode.
 *
 * `POST /api/v2/users/documents/search` returns one page of the documents the
 * acting user can see, filtered, sorted and keyset-paginated; the request is
 * snake_case (a pydantic body) and the rows are camelCase, like the file tree's
 * `FileEntry`. `GET /api/v2/users/documents/uploaders` is the People facet.
 */
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import type { FolderEntry } from "@/hooks/useFileTreeV2/fetchFiles";

export type DocumentStatusBucket =
  | "processing"
  | "completed"
  | "failed"
  | "locked";

export type UnreadableReason = "encrypted" | "no_signature";

export type DocumentSortBy = "name" | "uploaded_at" | "document_date";
export type DocumentSortOrder = "asc" | "desc";

export interface DocumentUploader {
  /** `me`, `user:<id>`, `email:<address>` or `system:<name>` — the same key
   *  the search endpoint's `uploaders` filter accepts. */
  key: string;
  label: string;
  /** An email-ingested upload's sender address, present only when the label
   *  is the sender's name and so leaves it out; absent for a bare-address
   *  sender, users, connectors and masked staff (#12075). */
  address?: string | null;
}

export interface LinkedEntity {
  kind: string;
  publicId: string;
  name: string;
  /** How the link was made: true when the system made every link to this
   *  record rather than a person (#6559 round 6). Absent on records that
   *  arrive from a search rather than from a document. */
  bySystem?: boolean;
}

/** Why a search hit is here when its name did not match: the custom value
 *  that did (#6559 round 8b). Null on a row whose name matched. */
export interface MatchedAttribute {
  columnId: number;
  columnName: string;
  value: string;
}

export interface DocumentRow {
  fileId: string;
  taskId: string | null;
  fileName: string;
  filePath: string[];
  driveId: string | null;
  fileType: string;
  status: string | null;
  isPasswordProtected: boolean;
  /** A stored password means the file is unlocked (#11856): it opens
   *  without asking, and reads as any other file while its task re-runs. */
  hasStoredPassword: boolean;
  /** Why an unreadable file (`fileType` "unreadable") cannot be read
   *  (#11277): password-protected Office, or bytes that are not the format
   *  its name says. Null for every other kind, unsupported included. */
  unreadableReason: UnreadableReason | null;
  /** An instant, rendered in the viewer's zone. */
  uploadedAt: string | null;
  /** A calendar day (`YYYY-MM-DD`), never converted. */
  documentDate: string | null;
  documentTypes: string[];
  uploadedBy: DocumentUploader | null;
  pageCount: number | null;
  linkedEntities: LinkedEntity[];
  /** How many records the document touches in all; `linkedEntities` is
   *  capped, so "+N" is counted from this. */
  linkedEntityCount: number;
  matchedAttribute?: MatchedAttribute | null;
}

/** One custom attribute clause on the wire (#6559 round 8b): exactly the
 *  operators the chips emit — enum and record `in`, boolean `is`, date and
 *  number `between`, text `contains` — ANDed with each other. */
export interface ColumnFilterWire {
  column_id: number;
  op: "in" | "is" | "between" | "contains";
  values?: string[];
  value?: boolean;
  from?: string | null;
  to?: string | null;
  text?: string;
}

export interface DocumentsSearchRequest {
  drive_id?: string | null;
  /** The folder to list; omitted or null means anywhere. */
  path?: string[] | null;
  /** False lists exactly `path`; true includes every folder beneath it. */
  recursive: boolean;
  /** Only the documents under any of these roots, each in its drive (null
   *  for none): the "Connected apps" search scope (round 17). */
  roots?: Array<{ drive_id: string | null; name: string }>;
  /** Only the documents in any of these drives (round 18). */
  drive_ids?: string[];
  q?: string;
  document_types?: string[];
  uploaders?: string[];
  uploaded_after?: string;
  uploaded_before?: string;
  document_date_from?: string;
  document_date_to?: string;
  status?: DocumentStatusBucket[];
  entity_public_ids?: string[];
  exclude_blob_kinds?: string[];
  /** Exactly these documents — the picker resolving where its initial
   *  selection lives. */
  blob_ids?: string[];
  column_filters?: ColumnFilterWire[];
  sort_by: DocumentSortBy;
  sort_order: DocumentSortOrder;
  cursor?: string | null;
  page_size?: number;
  /** False asks for no total: the response's `total` is then null. The
   *  total is an organization-wide count, and a listing that shows none
   *  (Home's Recently added) should not pay for it (#12073). */
  with_total?: boolean;
}

export type SuggestionKind = "opened" | "uploaded" | "attached" | "cited";

/** Why Home suggests a document: the newest thing the viewer did with it. A
 *  predicate about the viewer, always — never a name (Claude Design,
 *  round 7). */
export interface SuggestedReason {
  kind: SuggestionKind;
  /** An instant, rendered in the viewer's zone. Every reason is something the
   *  viewer did at a moment, so this is always present. */
  at: string;
}

export interface SuggestedDocument {
  document: DocumentRow;
  reason: SuggestedReason;
}

export interface DocumentsSearchResponse {
  files: DocumentRow[];
  nextCursor: string | null;
  /** Null when the request asked for no total (`with_total: false`). */
  total: number | null;
}

export const DOCUMENTS_SEARCH_PATH = "/api/v2/users/documents/search";
export const DOCUMENTS_UPLOADERS_PATH = "/api/v2/users/documents/uploaders";
export const DOCUMENTS_SUGGESTED_PATH = "/api/v2/users/documents/suggested";

export async function searchDocuments(
  body: DocumentsSearchRequest
): Promise<DocumentsSearchResponse> {
  const response = await fpFetch(`${BASE_API_URL_V2}${DOCUMENTS_SEARCH_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response?.ok) {
    throw new Error(`Documents search failed with status ${response?.status}`);
  }
  return (await response.json()) as DocumentsSearchResponse;
}

export const DOCUMENTS_FOLDERS_SEARCH_PATH =
  "/api/v2/users/documents/folders/search";

/** The folder hits of a search (#12088): the term and the scope the
 *  documents search carries, in the search request's own field names. */
export interface DocumentFoldersSearchRequest {
  q: string;
  path?: string[];
  drive_id?: string;
  roots?: Array<{ drive_id: string | null; name: string }>;
  drive_ids?: string[];
  exclude_blob_kinds?: string[];
  limit?: number;
}

/** The folders a term names, by name, up to the limit — each with its
 *  drive and no date, since a hit on a name resolves none. */
export async function searchDocumentFolders(
  body: DocumentFoldersSearchRequest
): Promise<FolderEntry[]> {
  const response = await fpFetch(
    `${BASE_API_URL_V2}${DOCUMENTS_FOLDERS_SEARCH_PATH}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!response?.ok) {
    throw new Error(`Folder search failed with status ${response?.status}`);
  }
  const data = (await response.json()) as { folders: FolderEntry[] };
  return data.folders;
}

export async function fetchDocumentUploaders(): Promise<DocumentUploader[]> {
  const response = await fpFetch(
    `${BASE_API_URL_V2}${DOCUMENTS_UPLOADERS_PATH}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response?.ok) {
    throw new Error(`Uploaders request failed with status ${response?.status}`);
  }
  const data = (await response.json()) as { uploaders: DocumentUploader[] };
  return data.uploaders;
}

/** What the viewer worked with recently, newest signal first, with why. */
export async function fetchSuggestedDocuments(
  limit: number
): Promise<SuggestedDocument[]> {
  const url = new URL(DOCUMENTS_SUGGESTED_PATH, BASE_API_URL_V2);
  url.searchParams.set("limit", String(limit));
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(
      `Suggestions request failed with status ${response?.status}`
    );
  }
  const data = (await response.json()) as { documents: SuggestedDocument[] };
  return data.documents;
}

/** A record as a Documents location (round 5): the heading's kind and name,
 *  and for a manager the funds "Include vehicle documents" adds. */
export interface DocumentRecord {
  publicId: string;
  kind: string;
  name: string;
  /** What the location states on arrival: how many documents are linked to
   *  this record, and how many of those links the system made. */
  linkedDocuments: number;
  autoLinkedDocuments: number;
}

interface DocumentRecordWire {
  public_id: string;
  kind: string;
  name: string;
  linked_documents: number;
  auto_linked_documents: number;
}

export async function fetchDocumentRecord(
  publicId: string
): Promise<DocumentRecord> {
  const url = new URL(
    `/api/v2/users/documents/records/${encodeURIComponent(publicId)}`,
    BASE_API_URL_V2
  );
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(`Record request failed with status ${response?.status}`);
  }
  const data = (await response.json()) as DocumentRecordWire;
  return {
    publicId: data.public_id,
    kind: data.kind,
    name: data.name,
    linkedDocuments: data.linked_documents,
    autoLinkedDocuments: data.auto_linked_documents,
  };
}

/** A record in the index or in Recent records. The count is the point: it is
 *  what separates a place from a dead end, and production says 97-99% of an
 *  organization's records are dead ends (#6559 round 6). */
export interface RecordSummary {
  publicId: string;
  kind: string;
  name: string;
  documentCount: number;
  /** A calendar day (`YYYY-MM-DD`), never converted. */
  latestDocumentDate: string | null;
  managerName: string | null;
  /** The manager's own address, for the Records page's Manager link (round
   *  16); a manager row has none. */
  managerPublicId: string | null;
}

export interface RecordsIndex {
  records: RecordSummary[];
}

/** How many records have documents, in all and by kind (round 16): the
 *  Records page's lede and segmented kinds. Every kind the page offers is
 *  present, at zero where nothing is linked. */
export interface RecordCounts {
  total: number;
  byKind: Record<string, number>;
}

interface RecordSummaryWire {
  public_id: string;
  kind: string;
  name: string;
  document_count: number;
  latest_document_date: string | null;
  manager_name: string | null;
  manager_public_id: string | null;
}

const toRecordSummary = (row: RecordSummaryWire): RecordSummary => ({
  publicId: row.public_id,
  kind: row.kind,
  name: row.name,
  documentCount: row.document_count,
  latestDocumentDate: row.latest_document_date,
  managerName: row.manager_name,
  managerPublicId: row.manager_public_id,
});

/** A drive as a place (round 17): the viewer's own, an organization's or a
 *  team's, and the connector that owns it where one does. */
export interface PlaceDrive {
  driveId: string;
  name: string;
  kind: "personal" | "organization" | "team";
  connector: string | null;
}

/** A connection as a place: the drive and root folder its files sit under,
 *  the site it comes from where there is one, and when it last synced. */
export interface PlaceConnection {
  key: string;
  label: string;
  driveId: string | null;
  path: string[];
  siteUrl: string | null;
  /** An instant, or null when the connector has never reported one. */
  lastSync: string | null;
}

export interface PlaceApp {
  app: string;
  label: string;
  connections: PlaceConnection[];
}

/** What the column lists (round 17, Claude Design): My Drive, the shared
 *  drives in their bands, and the connected apps — whose roots the drive
 *  each sits in lists first (round 18g), the column's row being a second
 *  door to the same place. */
export interface Places {
  drivesEnabled: boolean;
  myDrive: PlaceDrive | null;
  sharedDrives: PlaceDrive[];
  connectedApps: PlaceApp[];
}

export interface PlacesWire {
  drives_enabled: boolean;
  my_drive: PlaceDriveWire | null;
  shared_drives: PlaceDriveWire[];
  connected_apps: Array<{
    app: string;
    label: string;
    connections: Array<{
      key: string;
      label: string;
      drive_id: string | null;
      path: string[];
      site_url: string | null;
      last_sync: string | null;
    }>;
  }>;
}

interface PlaceDriveWire {
  drive_id: string;
  name: string;
  kind: "personal" | "organization" | "team";
  connector: string | null;
}

const toPlaceDrive = (wire: PlaceDriveWire): PlaceDrive => ({
  driveId: wire.drive_id,
  name: wire.name,
  kind: wire.kind,
  connector: wire.connector,
});

export const placesFromWire = (data: PlacesWire): Places => ({
  drivesEnabled: data.drives_enabled,
  myDrive: data.my_drive ? toPlaceDrive(data.my_drive) : null,
  sharedDrives: data.shared_drives.map(toPlaceDrive),
  connectedApps: data.connected_apps.map((app) => ({
    app: app.app,
    label: app.label,
    connections: app.connections.map((c) => ({
      key: c.key,
      label: c.label,
      driveId: c.drive_id,
      path: c.path,
      siteUrl: c.site_url,
      lastSync: c.last_sync,
    })),
  })),
});

export async function fetchPlaces(): Promise<Places> {
  const url = new URL("/api/v2/users/documents/places", BASE_API_URL_V2);
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(`Places request failed with status ${response?.status}`);
  }
  return placesFromWire((await response.json()) as PlacesWire);
}

export async function fetchRecordCounts(q: string): Promise<RecordCounts> {
  const url = new URL(
    "/api/v2/users/documents/records/counts",
    BASE_API_URL_V2
  );
  if (q) url.searchParams.set("q", q);
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(
      `Record counts request failed with status ${response?.status}`
    );
  }
  const data = (await response.json()) as {
    total: number;
    by_kind: Record<string, number>;
  };
  return { total: data.total, byKind: data.by_kind };
}

/** The index's sort (round 15): by its Documents or Latest document column,
 *  either way; newest document first is the default. */
export type RecordsSortKey = "latest" | "documents";
export interface RecordsSort {
  key: RecordsSortKey;
  direction: "asc" | "desc";
}

export interface RecordsIndexRequest {
  q?: string;
  kind?: string | null;
  sort?: RecordsSort;
  limit?: number;
  /** The page's first row (round 16: pages of fifty). */
  offset?: number;
}

export async function fetchRecordsIndex(
  request: RecordsIndexRequest = {}
): Promise<RecordsIndex> {
  const url = new URL("/api/v2/users/documents/records", BASE_API_URL_V2);
  if (request.q) url.searchParams.set("q", request.q);
  if (request.kind) url.searchParams.set("kind", request.kind);
  if (request.sort) {
    url.searchParams.set("sort", request.sort.key);
    url.searchParams.set("direction", request.sort.direction);
  }
  if (request.limit) url.searchParams.set("limit", String(request.limit));
  if (request.offset) url.searchParams.set("offset", String(request.offset));
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(`Records request failed with status ${response?.status}`);
  }
  const data = (await response.json()) as { records: RecordSummaryWire[] };
  return { records: data.records.map(toRecordSummary) };
}

/** Whether records are a destination in this organization at all: whether it
 *  has linked anything a viewer could follow. A share threshold needs
 *  per-client tuning and a re-decision every time coverage moves; this needs
 *  neither (#6559 round 7). */
export async function fetchRecordsEnabled(): Promise<boolean> {
  const url = new URL(
    "/api/v2/users/documents/records/enabled",
    BASE_API_URL_V2
  );
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) {
    throw new Error(
      `Records gate request failed with status ${response?.status}`
    );
  }
  const data = (await response.json()) as { enabled: boolean };
  return data.enabled;
}

/** The record a folder is named for, if exactly one is (#6559 round 7): the
 *  summary a search hit carries, so the page renders it as the same row
 *  (round 14). Null when no record wears the name, several do, or the one
 *  that does holds no document. */
export async function fetchFolderRecord(
  path: string[]
): Promise<RecordSummary | null> {
  const url = new URL(
    "/api/v2/users/documents/records/folder",
    BASE_API_URL_V2
  );
  const response = await fpFetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path }),
  });
  if (!response?.ok) {
    throw new Error(
      `Folder record request failed with status ${response?.status}`
    );
  }
  const data = (await response.json()) as { record: RecordSummaryWire | null };
  return data.record === null ? null : toRecordSummary(data.record);
}

/** The summaries behind the recents the viewer keeps (round 15): one
 *  recents list, the RMS search field's own, held in the browser; the ids
 *  resolve here, in the order asked, and an id Documents has no location
 *  for is left out. */
export async function fetchRecordSummaries(
  publicIds: string[]
): Promise<RecordSummary[]> {
  const url = new URL(
    "/api/v2/users/documents/records/summaries",
    BASE_API_URL_V2
  );
  const response = await fpFetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ public_ids: publicIds }),
  });
  if (!response?.ok) {
    throw new Error(
      `Record summaries request failed with status ${response?.status}`
    );
  }
  const data = (await response.json()) as { records: RecordSummaryWire[] };
  return data.records.map(toRecordSummary);
}

/** The viewer opened a document: a note for Home's suggestions. Fire and
 *  forget — the viewer must never wait on it or fail because of it. */
export function recordDocumentOpened(fileId: string): void {
  void fpFetch(
    `${BASE_API_URL_V2}/api/v2/users/documents/${encodeURIComponent(fileId)}/opened`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  ).catch(() => undefined);
}

interface ShallowSearchResponse {
  results: Array<{ entity: { id: string; kind: string; name: string } }>;
}

/** Records by name, of the given kinds: the Related to chip and the
 *  destinations column's Records group read this. */
export async function searchEntities(
  q: string,
  kinds: readonly string[]
): Promise<LinkedEntity[]> {
  const url = new URL("/api/v2/rms/entities/search/shallow", BASE_API_URL_V2);
  url.searchParams.set("q", q);
  for (const kind of kinds) url.searchParams.append("kind", kind);
  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response?.ok) return [];
  const data = (await response.json()) as ShallowSearchResponse;
  return data.results.map((r) => ({
    kind: r.entity.kind,
    publicId: r.entity.id,
    name: r.entity.name,
  }));
}
